/**
 * Link-preview resolution.
 *
 * Three layers, cheapest first:
 *
 *   1. the `linkpreview` entity — one row per URL, shared by every post and
 *      every viewer, so a link is scraped once and not once per render;
 *   2. the extraction worker (`EXPO_PUBLIC_LINK_PREVIEW_URL`) — the only thing
 *      in the system that fetches a URL a user typed;
 *   3. a provider fallback derived from the address alone, so a YouTube link
 *      still shows a thumbnail when the worker is unreachable or unconfigured.
 *
 * Every layer is allowed to fail. `resolveLinkPreview` returns null instead of
 * throwing, and the post renders with its plain link — a preview is never load-
 * bearing. In particular, an unconfigured worker or a `linkpreview` entity that
 * has not been created in the portal yet degrades quietly rather than erroring
 * on every post in the feed.
 */

import {
  isLinkPreviewType,
  LinkPreview,
  StoredLinkPreview,
} from "@/src/models/link-preview.model";
import gqlClient from "@/src/services/api/graphql.client";
import {
  buildProviderFallback,
  detectLinkPreviewType,
} from "@/src/utils/link-preview-providers.utils";
import { validateFetchableUrl } from "@/src/utils/link-preview-safety.utils";
import {
  detectMediaKind,
  extractFirstUrl,
  getDomain,
  normalizeUrl,
  sanitizeImageUrl,
  sanitizeMetaText,
} from "@/src/utils/link-preview.utils";
import {
  GET_LINK_PREVIEWS,
  INSERT_LINK_PREVIEW,
  UPDATE_LINK_PREVIEW,
} from "./link-preview.query";

/** How long a stored preview is trusted before the worker is asked again. */
export const PREVIEW_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** The worker has its own 5s budget; this is the client's patience for it. */
const REQUEST_TIMEOUT_MS = 8_000;

const extractorEndpoint = (): string | null =>
  process.env.EXPO_PUBLIC_LINK_PREVIEW_URL || null;

/**
 * Coerce anything preview-shaped — a gateway row or a worker response — into a
 * `LinkPreview`, re-sanitising as it goes. Stored rows were cleaned on the way
 * in, but a render path that trusts whatever the database hands back is one
 * schema change away from rendering someone's markup.
 */
const toLinkPreview = (raw: unknown, url: string): LinkPreview | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;

  const title = sanitizeMetaText(
    typeof row.title === "string" ? row.title : null,
    200,
  );
  const description = sanitizeMetaText(
    typeof row.description === "string" ? row.description : null,
    300,
  );
  const imageUrl = sanitizeImageUrl(
    typeof row.imageUrl === "string" ? row.imageUrl : null,
    url,
  );
  const videoId = typeof row.videoId === "string" ? row.videoId : null;

  // A card with no title, no description and no image is an empty box; the
  // plain link reads better. A video preview survives on its thumbnail alone.
  if (!title && !description && !imageUrl && !videoId) return null;

  return {
    url: typeof row.url === "string" && row.url ? row.url : url,
    type: isLinkPreviewType(row.type) ? row.type : "webpage",
    title,
    description,
    imageUrl,
    siteName: sanitizeMetaText(
      typeof row.siteName === "string" ? row.siteName : null,
      80,
    ),
    domain: typeof row.domain === "string" && row.domain ? row.domain : getDomain(url),
    videoId,
  };
};

const isStale = (row: StoredLinkPreview): boolean => {
  const timestamp = row.LastUpdatedDate ?? row.CreatedDate;
  if (!timestamp) return false;
  const age = Date.now() - new Date(timestamp).getTime();
  return Number.isFinite(age) ? age > PREVIEW_MAX_AGE_MS : false;
};

/* ------------------------------------------------------------ stored layer */

type GetLinkPreviewsResponse = {
  getlinkpreviews?: { items?: StoredLinkPreview[] | null } | null;
};

export const readStoredPreview = async (
  url: string,
): Promise<StoredLinkPreview | null> => {
  try {
    const client = await gqlClient();
    const response = (await client.request(GET_LINK_PREVIEWS, {
      where: { url: { eq: url } },
      pageNo: 1,
      pageSize: 1,
    })) as GetLinkPreviewsResponse;
    return response?.getlinkpreviews?.items?.[0] ?? null;
  } catch {
    // Entity missing from the portal schema, offline, or an auth blip — the
    // worker is still worth trying.
    return null;
  }
};

const writeStoredPreview = async (
  preview: LinkPreview,
  existingItemId?: string,
): Promise<void> => {
  try {
    const client = await gqlClient();
    const input = {
      type: preview.type,
      title: preview.title,
      description: preview.description,
      imageUrl: preview.imageUrl,
      siteName: preview.siteName,
      domain: preview.domain,
      videoId: preview.videoId,
    };
    if (existingItemId) {
      await client.request(UPDATE_LINK_PREVIEW, { itemId: existingItemId, ...input });
    } else {
      await client.request(INSERT_LINK_PREVIEW, { url: preview.url, ...input });
    }
  } catch {
    // Caching is an optimisation. A failed write costs a re-extraction later.
  }
};

/* --------------------------------------------------------- extractor layer */

/**
 * Ask the worker for metadata. The app deliberately has no fallback that
 * fetches the page itself: scraping belongs on the server, and a client-side
 * fetch would be both CORS-blocked on web and an SSRF hole nowhere near the
 * checks the worker runs.
 */
export const fetchPreviewFromExtractor = async (
  url: string,
): Promise<LinkPreview | null> => {
  const endpoint = extractorEndpoint();
  if (!endpoint) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${endpoint}?url=${encodeURIComponent(url)}`,
      { signal: controller.signal, headers: { Accept: "application/json" } },
    );
    if (!response.ok) return null;
    return toLinkPreview(await response.json(), url);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

/* ---------------------------------------------------------------- resolver */

/**
 * The preview for a single URL, or null when there is nothing worth showing.
 * Safe to call on every render — repeat calls hit the react-query cache, and
 * repeat misses hit the stored row rather than the network.
 */
export const resolveLinkPreview = async (
  rawUrl: string,
): Promise<LinkPreview | null> => {
  const validated = validateFetchableUrl(rawUrl);
  if (!validated.safe) return null;

  const url = normalizeUrl(rawUrl);
  const stored = await readStoredPreview(url);

  if (stored && !isStale(stored)) {
    return toLinkPreview(stored, url);
  }

  const fetched = await fetchPreviewFromExtractor(url);
  if (fetched) {
    void writeStoredPreview(fetched, stored?.ItemId);
    return fetched;
  }

  // Extraction failed. A stale row still beats no card at all.
  if (stored) return toLinkPreview(stored, url);

  return buildProviderFallback(url);
};

/**
 * Extract and cache the preview for a post's content without blocking anything.
 *
 * Called once the post is created, so the metadata is fetched and stored while
 * the author is still looking at the composer closing — by the time the feed
 * renders the post, the row is usually already there. Post creation never waits
 * on this and never fails because of it.
 */
export const primeLinkPreviewFromContent = async (
  content: string | undefined | null,
): Promise<void> => {
  const url = extractFirstUrl(content ?? "");
  if (!url || detectMediaKind(url) !== "page") return;
  if (!detectLinkPreviewType(url)) return;
  try {
    await resolveLinkPreview(url);
  } catch {
    // Best effort: the post is already created and renders fine without a card.
  }
};
